import api, { isApiError } from '@/services/api';
import type { SchedulingMethod } from '@/features/products/types/products.types';
import type {
    CollectionTarget,
    SchedulePreview,
    SchedulePreviewParams,
    ScheduleFrequency,
    ScheduleMeta,
    ScheduleRow,
} from '../types/schedule.types';

// Answers what a PERSISTED key means, not what an admin may choose:
// `item_based_with_deposit` is no longer configurable, but resumed quotes still carry it.
export const ITEM_BASED_METHOD_KEY = 'item_based';
const ITEM_BASED_WITH_DEPOSIT_METHOD_KEY = 'item_based_with_deposit';
export const ITEM_BASED_FAMILY_KEYS: ReadonlySet<string> = new Set([
    ITEM_BASED_METHOD_KEY,
    ITEM_BASED_WITH_DEPOSIT_METHOD_KEY,
]);

/** The only method key `isDepositBearingSelection` excludes outright — a full payment
 *  never carries a deposit, regardless of `allowDeposit`. */
export const PAID_IN_FULL_METHOD_KEY = 'paid_in_full';

/**
 * Same persisted-key rule as above: `deposit_plus_even` is no longer configurable, but resumed
 * quotes still carry it. Disjointness from the item-based set is pinned by behaviour in tests.
 */
export const EVEN_MONTHLY_FAMILY_KEYS: ReadonlySet<string> = new Set([
    'even_monthly',
    'deposit_plus_even',
]);

const SCHEDULE_PREVIEW_PATH = '/quotes/schedule/preview';

interface SchedulePreviewApiResponse {
    success: boolean;
    message?: string;
    data: {
        schedule: ScheduleRow[];
        meta: ScheduleMeta;
        num_payments: number;
    };
}

/**
 * Re-exported for backward compatibility — the canonical definition now
 * lives in `../utils/money.ts` (a transport-free pure util) so it can be
 * imported by schemas/services without pulling in this module's `api.ts`
 * dependency. See `../utils/money.ts` for the conversion rule itself.
 */
export { toMinorUnits } from '../utils/money';

/** Deposit-bearing iff `allowDeposit` or a legacy `*_with_deposit` template survives —
 *  permanent, since dropping it loses the deposit on every resumed quote. */
export const isDepositBearingSelection = (
    method: SchedulingMethod | null,
    allowDeposit: boolean,
): boolean => {
    if (method === null || method.key === PAID_IN_FULL_METHOD_KEY) return false;
    if (allowDeposit) return true;
    return method.elemis_params?.has_deposit === true && method.key !== ITEM_BASED_METHOD_KEY;
};

/**
 * Merges a scheduling method's `elemis_params` template (snake_case, as
 * resolved by the backend — see `SchedulingMethod.elemis_params`) with the
 * runtime amount/deposit/collection-targets (already integer minor units)
 * into the camelCase `SchedulePreviewParams` boundary shape. Pure — no
 * transport, no React.
 *
 * `depositAmount` is only ever included when the method actually takes a
 * deposit (`has_deposit: true`) AND a runtime deposit value was supplied —
 * omitting it while `hasDeposit: true` preserves the contract's equal-split
 * default (§5).
 *
 * `collectionTargets` is only ever included when the method key is a member
 * of the item-based FAMILY (`item_based` OR `item_based_with_deposit`) AND a
 * non-empty, already-built target list was supplied via
 * `ctx.collectionTargets` (built by `buildCollectionTargets`,
 * `./collectionTargets.ts`, Phase D / #475-D, extended Phase 2 / #618).
 *
 * `numInstalments` for the item-based family (#730) is DERIVED client-side
 * (`alignCollectionTargets`, `./collectionPeriods.ts`) from the final
 * collection target's period, never templated — elemis anchors instalment N
 * at request-date + N months and requires the final target to land exactly
 * on the final instalment (contract §8), which the fixed default of 12 only
 * satisfies for a basket ending exactly 12 months out. The derived count is
 * sent **iff** the targets it was derived from are sent (`hasTargets` below),
 * and it deliberately takes precedence over `template.num_instalments` so a
 * misconfigured product config cannot override a count elemis requires to
 * match the final target exactly. Every non-item-based method is unaffected —
 * `ctx.numInstalments` is simply never read outside the family gate.
 *
 * `numInstalments` for the even-monthly family (`even_monthly`,
 * `deposit_plus_even` — #740, `EVEN_MONTHLY_FAMILY_KEYS`) is likewise
 * DERIVED client-side (`deriveEvenMonthlySchedule`, `./evenMonthlyTerm.ts`)
 * from the basket's real term, and likewise takes precedence over
 * `template.num_instalments` so a misconfigured product config cannot
 * override it. Unlike the item-based family it never routes
 * `collectionTargets` — the even-monthly family sends no targets at all —
 * and it is sent only when `transmittedInstalments` yields a count; when it
 * does not, the field is omitted and elemis falls back to its documented 12.
 * The two family key-sets (`ITEM_BASED_FAMILY_KEYS`, module-private, and
 * `EVEN_MONTHLY_FAMILY_KEYS`, exported) are DISJOINT — pinned behaviourally
 * in `schedulePreview.test.ts`, not by comparing the sets — so routing a
 * supplied count for the even-monthly family can never weaken the item-based
 * family's "count sent iff targets sent" invariant above.
 *
 * D8 hard constraint (contract §8 / elemis #942): PLAIN `item_based` never
 * combines `collection_targets` with a deposit sourced from a misconfigured
 * legacy template alone — elemis rejects that combination for the no-deposit
 * key. `item_based_with_deposit`, and `item_based` when the PRODUCT sets
 * `allow_deposit` (the derived "Variable with Deposit"), deliberately
 * DO combine both, per elemis's shipped period-0 model (elemis #942 / PR
 * #944): the deposit seeds the opening cumulative so the first segment
 * collects `V1 - deposit`, with the invariant `deposit < first collection
 * target` (mirrored client-side in `DepositInputSchema`/`PaymentStep`, never
 * trusted from the template alone). The guard against the misconfigured-
 * template case now lives in the CALLER's `isDepositBearingSelection`
 * (`ctx.isDepositBearing` below), not here — see D3.
 */
export const mapMethodToParams = (
    method: SchedulingMethod,
    ctx: {
        amountMinor: number;
        depositMinor?: number;
        /** Already evaluated by the caller via isDepositBearingSelection.
         *  Never re-derived here — one predicate, one evaluation site. */
        isDepositBearing?: boolean;
        collectionTargets?: CollectionTarget[];
        /** Derived instalment count. For the item-based family (#730),
         *  consumed only alongside the targets it was derived from. For the
         *  even-monthly family (#740), consumed whenever supplied — that
         *  family never sends targets, so there is nothing for it to be
         *  "alongside". Ignored for every other method. */
        numInstalments?: number;
        /** Minor units. Sent only alongside a deposit-bearing method's own
         *  `hasDeposit: true`, mirroring `prohibited_unless:hasDeposit,true`. */
        dealerCollectedDepositAmount?: number;
    },
): SchedulePreviewParams => {
    const template = method.elemis_params ?? {};
    // The whole item-based family routes collection_targets.
    const isItemBasedFamily = ITEM_BASED_FAMILY_KEYS.has(method.key);
    // Only true once there is something to send — mirrors the guard the
    // collectionTargets assignment below already applies, computed once so
    // the numInstalments and collectionTargets decisions can never disagree.
    const hasTargets =
        isItemBasedFamily && !!ctx.collectionTargets && ctx.collectionTargets.length > 0;
    const params: SchedulePreviewParams = {
        amount: ctx.amountMinor,
    };

    // #740: the derived count is sent for the even-monthly family too, and —
    // as for the item-based family since #730 — takes precedence over
    // `template.num_instalments`, so a misconfigured product config cannot
    // override a count derived from the real plan term. The two family
    // key-sets are DISJOINT (pinned behaviourally: an item-based key with no
    // targets still ignores a supplied count — see schedulePreview.test.ts),
    // so this cannot weaken #730's "the count is sent iff the targets it was
    // derived from are sent" invariant for the item-based family.
    const useDerivedCount =
        (hasTargets || EVEN_MONTHLY_FAMILY_KEYS.has(method.key)) &&
        typeof ctx.numInstalments === 'number';

    if (useDerivedCount) {
        // #730/#740: for the item-based family the count is DERIVED from the
        // final collection target's period; for the even-monthly family it
        // is DERIVED from the basket's real term. Neither is templated.
        // Deliberately takes precedence over template.num_instalments so a
        // misconfigured product config cannot override a count elemis
        // requires (item-based, probes B/C/J) or a count that reflects the
        // real plan term (even-monthly, #740).
        params.numInstalments = ctx.numInstalments;
    } else if (typeof template.num_instalments === 'number') {
        params.numInstalments = template.num_instalments;
    }

    if (typeof template.frequency === 'string') {
        params.frequency = template.frequency as ScheduleFrequency;
    }

    // Evaluated once by the caller (isDepositBearingSelection), never re-derived
    // from the template here.
    if (ctx.isDepositBearing === true) {
        // An explicit £0 is "no deposit", so has_deposit:false. A blank field (undefined) keeps
        // hasDeposit:true for elemis's equal-split default.
        if (ctx.depositMinor === 0) {
            params.hasDeposit = false;
        } else {
            params.hasDeposit = true;
            if (ctx.depositMinor !== undefined) {
                params.depositAmount = ctx.depositMinor;
            }
            if (ctx.dealerCollectedDepositAmount !== undefined) {
                params.dealerCollectedDepositAmount = ctx.dealerCollectedDepositAmount;
            }
        }
    }

    // For the whole item-based family, and only when there is something to send.
    if (hasTargets) {
        params.collectionTargets = ctx.collectionTargets;
    }

    return params;
};

/**
 * Inverse of the deposit mapping above, for re-pricing a CONFIRMED selection: `hasDeposit: false`
 * is an explicit £0, which must stay 0 and not fall back to the equal-split default.
 */
export const confirmedDepositMinor = (params: SchedulePreviewParams): number | undefined =>
    params.hasDeposit === false ? 0 : params.depositAmount;

/**
 * Maps a user-facing error message for a failed schedule preview. elemis
 * rejections are deliberately opaque (contract §4 — no enumeration oracle),
 * so a 422 always collapses to the same generic message regardless of cause;
 * 502/503 (transport failure / timeout) get a distinct "unavailable" message.
 * Never forwards raw elemis/backend internals to the UI.
 */
const normalisePreviewError = (error: unknown): Error => {
    if (isApiError(error)) {
        if (error.status === 422) {
            return new Error('Please check the details and try again.');
        }
        if (error.status === 502 || error.status === 503) {
            return new Error('The payment schedule service is temporarily unavailable.');
        }
    }
    return new Error('Unable to preview the payment schedule.');
};

/**
 * POSTs the (already minor-unit) preview params to the quote-service proxy
 * and normalises the response to the camelCase `SchedulePreview` shape
 * (only `num_payments -> numPayments` is renamed; `schedule`/`meta` field
 * names are passed through as the backend returns them). Components must not
 * call this (or the axios client) directly — `useSchedulePreview` is the
 * supported caller and wraps this behind React Query.
 */
export const previewSchedule = async (params: SchedulePreviewParams): Promise<SchedulePreview> => {
    const body: Record<string, unknown> = {
        amount: Math.round(params.amount),
    };

    if (params.hasDeposit !== undefined) body.hasDeposit = params.hasDeposit;
    if (params.depositAmount !== undefined) body.depositAmount = Math.round(params.depositAmount);
    if (params.numInstalments !== undefined) body.numInstalments = params.numInstalments;
    if (params.frequency !== undefined) body.frequency = params.frequency;
    if (params.collectionTargets !== undefined) body.collectionTargets = params.collectionTargets;
    if (params.dealerCollectedDepositAmount !== undefined) {
        body.dealerCollectedDepositAmount = Math.round(params.dealerCollectedDepositAmount);
    }

    try {
        const response = await api.post<SchedulePreviewApiResponse>(SCHEDULE_PREVIEW_PATH, body);

        if (!response.data.success) {
            throw new Error('Unable to preview the payment schedule.');
        }

        return {
            schedule: response.data.data.schedule,
            meta: response.data.data.meta,
            numPayments: response.data.data.num_payments,
        };
    } catch (error) {
        throw normalisePreviewError(error);
    }
};
